import { Button } from "@/components/atoms/Button";
import { Label } from "@/components/atoms/Label";
import { Cross2Icon, EyeOpenIcon, Pencil1Icon } from "@radix-ui/react-icons";
import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import MDXEditorInput from "../MDXEditorInput";
import type { BaseFieldProps } from "./types";

// MDX Editor Popup Component
const MDXEditorPopup: React.FC<{
	fieldName: string;
	label?: string;
	value: string;
	onChange: (value: string) => void;
	onClose: () => void;
	hasError: boolean;
	validationMessage?: string;
}> = ({
	fieldName,
	label,
	value,
	onChange,
	onClose,
	hasError,
	validationMessage,
}) => {
		const [editorValue, setEditorValue] = useState(value);

		const handleSave = () => {
			onChange(editorValue);
			onClose();
		};

		const handleCancel = () => {
			setEditorValue(value); // Reset to original value
			onClose();
		};

		return (
			<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
				<div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
					{/* Header */}
					<div className="flex items-center justify-between p-6 border-b border-gray-2">
						<div>
							<h3 className="text-heading-4-semibold font-semibold text-black">
								{label || "Rich Text Editor"}
							</h3>
							{hasError && (
								<p className="text-secondary-main text-body-4-medium mt-1">
									{validationMessage || "This field is required"}
								</p>
							)}
						</div>
						<button
							onClick={onClose}
							className="text-gray-5 hover:text-gray-7 transition-colors"
						>
							<Cross2Icon className="w-6 h-6" />
						</button>
					</div>

					{/* Editor Content */}
					<div className="flex-1 p-6 overflow-hidden">
						<div className="h-full">
							<MDXEditorInput value={editorValue} onChange={setEditorValue} />
						</div>
					</div>

					{/* Footer */}
					<div className="flex items-center justify-end gap-4 p-6 border-t border-gray-2">
						<Button
							type="button"
							onClick={handleCancel}
							classType="admin"
							className="bg-gray-3 text-gray-7 hover:bg-gray-4"
						>
							Cancel
						</Button>
						<Button type="button" onClick={handleSave} classType="admin">
							Save
						</Button>
					</div>
				</div>
			</div>
		);
	};

// Preview Popup Component
const PreviewPopup: React.FC<{
	value: string;
	onClose: () => void;
}> = ({ value, onClose }) => {
	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
			<div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[80vh] flex flex-col">
				{/* Header */}
				<div className="flex items-center justify-between py-2 px-6 border-b border-gray-2">
					<h3 className="text-heading-4-semibold font-semibold text-black">
						Content Preview
					</h3>
					<button
						onClick={onClose}
						className="text-gray-5 hover:text-gray-7 transition-colors"
					>
						<Cross2Icon className="w-6 h-6" />
					</button>
				</div>

				{/* Preview Content */}
				<div className="flex-1 p-6 overflow-y-auto">
					<div className="prose prose-sm max-w-none">
						<ReactMarkdown
							remarkPlugins={[remarkGfm]}
							rehypePlugins={[rehypeRaw]}
							components={{
								table: (props) => (
									<table
										className="table-auto border-collapse border border-gray-400 w-full"
										{...props}
									/>
								),
								thead: (props) => <thead className="bg-gray-100" {...props} />,
								th: (props) => (
									<th
										className="border border-gray-400 px-4 py-2 text-left"
										{...props}
									/>
								),
								td: (props) => (
									<td className="border border-gray-400 px-4 py-2" {...props} />
								),
							}}
						>
							{value}
						</ReactMarkdown>
					</div>
				</div>

				{/* Footer */}
				<div className="flex items-center justify-end gap-4 p-6 border-t border-gray-2">
					<Button
						type="button"
						onClick={onClose}
						classType="admin"
						className="bg-gray-3 text-gray-7 hover:bg-gray-4"
					>
						Close
					</Button>
				</div>
			</div>
		</div>
	);
};

// Main MDX Editor Field Component
const MDXEditorField: React.FC<BaseFieldProps> = ({
	fieldName,
	label,
	placeholder,
	disabled,
	className,
	required,
	hasError,
	validationMessage,
	formData,
	handleChange,
}) => {
	const [showEditor, setShowEditor] = useState(false);
	const [showPreview, setShowPreview] = useState(false);
	const currentValue = formData[fieldName] || "";

	return (
		<div key={fieldName} className="flex-1 min-w-80 max-w-md mb-4">
			{label && (
				<Label
					htmlFor={fieldName}
					className="block text-body-3-semibold font-semibold text-black mb-2"
				>
					{label}
					{required && <span className="text-secondary-main ml-1">*</span>}
				</Label>
			)}

			{/* Buttons */}
			<div className="flex gap-2 mb-2">
				<Button
					type="button"
					onClick={() => setShowEditor(true)}
					disabled={disabled}
					classType="admin"
					className={`flex-1 justify-center ${hasError ? "border-secondary-main" : ""}`}
				>
					<div className="flex items-center gap-2">
						<Pencil1Icon className="w-4 h-4" />
						<span>{currentValue ? "Edit Content" : "Add Content"}</span>
					</div>
				</Button>

				{currentValue && (
					<Button
						type="button"
						onClick={() => setShowPreview(true)}
						disabled={disabled}
						classType="admin"
						className="flex-1 justify-center bg-gray-3 text-gray-7 hover:bg-gray-4"
					>
						<div className="flex items-center gap-2">
							<EyeOpenIcon className="w-4 h-4" />
							<span>Preview</span>
						</div>
					</Button>
				)}
			</div>

			{/* Preview of current content */}
			{currentValue && (
				<div className="mt-2 p-2 bg-gray-1 rounded-md border border-gray-3">
					<p className="text-body-4-medium text-gray-6 mb-1">Content Preview:</p>
					<div className="bg-gray-6 text-gray-1 p-2 rounded font-mono text-xs overflow-x-auto max-h-20 overflow-y-auto">
						<pre className="whitespace-pre-wrap break-words">{currentValue}</pre>
					</div>
				</div>
			)}

			{/* Popup Editor */}
			{showEditor && (
				<MDXEditorPopup
					fieldName={fieldName}
					label={label}
					value={currentValue}
					onChange={(value) => handleChange(fieldName, value)}
					onClose={() => setShowEditor(false)}
					hasError={hasError}
					validationMessage={validationMessage}
				/>
			)}

			{/* Preview Popup */}
			{showPreview && (
				<PreviewPopup value={currentValue} onClose={() => setShowPreview(false)} />
			)}

			{hasError && (
				<p className="text-secondary-main text-body-4-medium mt-1">
					{validationMessage || "This field is required"}
				</p>
			)}
		</div>
	);
};

export default MDXEditorField;


